import { useEffect } from 'react';
import { AlertCircle, Target } from 'lucide-react';
import { useQuiz } from '../context/QuizContext';
import { LinhaDoTempoMinigame } from '../components/minigames/LinhaDoTempoMinigame';
import { RostoHistoriaMinigame } from '../components/minigames/RostoHistoriaMinigame';
import { ConexaoSaberesMinigame } from '../components/minigames/ConexaoSaberesMinigame';
import { DueloAncestraisMinigame } from '../components/minigames/DueloAncestraisMinigame';
import { SoundManager } from '../utils/SoundManager';
import { CharacterPortrait } from '../components/CharacterPortrait/CharacterPortrait';
import { GameShell } from '../components/layout/GameShell';
import { getBiomeTheme } from '../utils/biomeTheme';

export function MinigameScreen() {
  const { state, dispatch } = useQuiz();
  const desafio = state.desafioAtual;

  if (!desafio) return null;

  const handleComplete = (sucesso: boolean) => {
    if (desafio.tipo === 'DUELO_ANCESTRAIS') {
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: {
          message: sucesso
            ? 'Aposta aceita! Próxima pergunta em modo Tudo ou Nada, com Axé desta pergunta em dobro.'
            : 'Duelo recusado. Jogo segue normalmente.',
          type: sucesso ? 'duelo' : 'info',
        },
      });
    }
    dispatch({ type: 'COMPLETE_MINIGAME', payload: { sucesso } });
  };

  useEffect(() => {
    if (!desafio || desafio.tipo !== 'DUELO_ANCESTRAIS') return;
    SoundManager.playDueloProposto();
  }, [desafio]);

  const renderMinigame = () => {
    switch (desafio.tipo) {
      case 'LINHA_DO_TEMPO':
        return <LinhaDoTempoMinigame desafio={desafio} onComplete={handleComplete} />;
      case 'ROSTO_HISTORIA':
        return <RostoHistoriaMinigame desafio={desafio} onComplete={handleComplete} />;
      case 'CONEXAO_SABERES':
        return <ConexaoSaberesMinigame desafio={desafio} onComplete={handleComplete} />;
      case 'DUELO_ANCESTRAIS':
        return <DueloAncestraisMinigame desafio={desafio} onComplete={handleComplete} />;
      default:
        return (
          <div className="w-full space-y-3 flex flex-col items-center justify-center p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 border-dashed">
            <AlertCircle size={28} className="text-slate-600 mb-1 opacity-50" />
            <p className="text-slate-400 font-cyber tracking-wider text-[10px] uppercase">Simulação de Combate Necessária</p>
            <button
              onClick={() => handleComplete(true)}
              className="btn-cyber w-full sm:w-72 py-2.5 rounded-xl text-slate-950 font-black tracking-widest bg-amber-500 hover:bg-amber-400 border border-amber-300 shadow-neon-amber uppercase text-xs transition-all"
            >
              Simular Vitória (+{desafio.recompensa.moedas} Axé)
            </button>
          </div>
        );
    }
  };

  return (
    <GameShell maxWidth="xl" maxHeightCeiling="max-h-[100dvh]" frameRgb={getBiomeTheme(state.faseAtual).rgb}>
      {/* Painel intrínseco: altura do conteúdo (com teto), centralizado por
          my-auto. O vazio da tela fica FORA do vidro como moldura simétrica,
          nunca dentro do painel (L5 — medido 222–438px de vão interno antes). */}
      <div className="glass-panel p-3 sm:p-4 rounded-2xl border-sky-500/30 shadow-[0_0_15px_rgba(14,165,233,0.1)] stage-module items-center w-full max-h-[min(100%,44rem)] my-auto text-center relative overflow-hidden group">

        {/* Background Decorativo */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.03)_1px,transparent_1px)] bg-[size:20px_20px] opacity-20 pointer-events-none"></div>

        {/* Top Anchor: Retrato compact e badge do desafio */}
        <div className="flex flex-col items-center gap-1 relative z-10 shrink-0 w-full">
          <div className="flex items-center justify-center gap-2">
            <CharacterPortrait fase={state.faseAtual} variant="compact" showLabel={false} />
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-sky-400 font-cyber font-bold tracking-wider text-[9px] uppercase flex items-center gap-1 border border-sky-500/30 px-2 py-0.5 rounded-full bg-sky-900/20">
                <Target size={11} /> Interceptação de Frequência
              </span>
              {state.dueloAtivo && (
                <span className="bg-amber-900/40 text-amber-500 border border-amber-500/50 px-2 py-0.5 rounded-full text-[9px] font-cyber font-bold tracking-wider flex items-center shadow-neon-amber">
                  💥 TUDO OU NADA ATIVO!
                </span>
              )}
            </div>
          </div>

          <h2 className="text-fluid-title font-cyber font-black text-white tracking-wider uppercase drop-shadow-md">
            {desafio.tipo.replace(/_/g, ' ')}
          </h2>
        </div>

        {/* Stage: o minigame define a própria altura; o gap do stage-module
            dá o respiro contido entre cabeçalho e desafio */}
        <div className="w-full text-left relative z-10 min-h-0">
          {renderMinigame()}
        </div>
      </div>
    </GameShell>
  );
}
